import type { FieldValue } from './fieldValue';
import type { AsyncOperationStateValue } from './asyncOperationItemCreationTypes';
import type { ItemMetaData, ItemMetaDataDto } from './fieldValue';
import type { RestoreMediaRequestDto } from './restoreMediaRequestDto';
import type { ItemAccessControl } from './itemAccessControlDto';
import type { MediaAvailability } from './mediaAvailability';
import type { FileLocation } from './fileLocation';
import type { EncoderType } from '../encoder/encoderType';

export type AnalyticsPluginConfig = {
  enabled: boolean;
  id: string;
  inputMetadata: Record<string, FieldValue>;
  status: AsyncOperationStateValue | undefined;
  msg: string | undefined;
};

export type CloudEncodingFormat = 'h264' | 'prores' | 'mpegts';
export type EditProxyFormat = CloudEncodingFormat;


export interface FocusPoint {
  x: number;
  y: number;
}

export interface Ingest {
  source: FileLocation,
  agent?: string,
}

/**
 * When an item was ingested by Kelda, this object describes the request.
 */
export interface KeldaIngest {
  requestId: string;
}

/**
 * This describes a set of png images derived from the original content,
 * like a pdf. The pages can be accessed via the media segment url api using
 * StreamRoleId.PngPageProxy
 */
export interface PagedProxy {
  numberOfPages: number;
  width: number;
  height: number;
}

export interface SubclipParams {
  /**
   * Start time of the subclip in milliseconds
   */
  startTime: number;
  /**
   * End time of the subclip in milliseconds
   */
  endTime: number;
  fileName?: string;
  dstStorageId?: string;
  dstKeldaId?: string;
  shouldCreateVisibleItem?: boolean;
  targetMimirFolderId?: string;

  /**
   * @deprecated Use itemCreationRequest instead. If both subclipItemMetadata and itemCreationRequest
   * are specified, itemCreationRequest would take precedence.
   */
  subclipItemMetadata?: ItemMetaData;

  /**
   * Any specific attributes to be applied to the new item (e.g., the analytics choices).
   */
  itemCreationRequest?: SubclipItemInitializationProperties;
}

export type PendingSubclipOperation = SubclipParams & {
  subclipItemId: string;
  transferRequestId?: string;
  executedBy?: string;
};

/**
 * This type used to be a partial ItemCreationRequestDto, but very little of the
 * type was actually used for type-checking. Because this caused a lot of
 * unnecessary transitive imports, the type definition has now been stripped
 * down to only the what is used.
 *
 * TODO: I'm concerned about the contents of this type being indiscriminately
 * spread into the item entity of newly create subclips. There might be some
 * attack vector involving overwriting internal-only fields of items through the
 * use of subclipping here.
 */
interface SubclipItemInitializationProperties {
  metadata?: ItemMetaDataDto | undefined;
  visibleTo?: ItemAccessControl;
}

/*
  TODO: Gather the shared superset of the properties of all Item DTO
  representations that are considered part of the API surface here, rather than
  having multiple definitions of the same properties spread around.
*/

export interface GeneratedContentLocation {
  /**
   * If undefined, the backend will assume
   * that this is in our own generated media bucket
   * under '<item id>/'.
   *
   * If it is 'MIMIR_GENERATED_MEDIA_BUCKET', the backend will
   * assume that this is in our own generated media bucket
   * under '<tenant id>/<item id>/'.
   *
   * Otherwise, this is the "id" of a GeneratedMediaLocationEntity which can be
   * retrieved using
   * GeneratedMediaLocationMapperOps.getGeneratedMediaLocation().
   */
  generatedMediaLocationId?: string;

  thumbnail?: FileLocation;

  // If generatedMediaLocationId refers to a custom bucket, this field specifies
  // the path (under any prefix specified by the GeneratedMediaLocationEntity)
  // where the content lives.
  // If undefined, the item ID should be used.
  contentPrefix?: string
}

export interface CloudIngestLocation {
  ingestSourceS3Bucket?: string;
  ingestSourceFullPath?: string;
  ingestSourceStorageId?: string;
}

export interface CloudArchiveLocation {
  archiveS3Bucket?: string;
  archiveLocationFullPath?: string;
}

export const possibleHighresDeletionStates = <const>['scheduled',  'in-progress',  'processed',  'failed',];

export type HighresDeletionState = typeof possibleHighresDeletionStates[number];

export interface ItemHighresLocationProps {
  /**
   * Corresponds to the 'ingest' property of the item
   */
  ingest: Ingest | undefined;
  /**
   * Stores 'ingest*' properties of the item that are used by cloud S3/Azure/GCS bucket ingest
   */
  cloudIngest: CloudIngestLocation | undefined;
  /**
   * Exists if highRes file has been transferred to an S3 bucket as part of the archive operation
   */
  archive: CloudArchiveLocation | undefined;
  /**
   * Media availability object used to store information about segmented highRes
   */
  mediaAvailability: MediaAvailability | undefined;
}

/**
 * Defines information related to highres deletion operation
 */
export interface HighresDeletion {
  /**
   * Original location information of the highRes at the time of creating the deletion request
   */
  highresLocation: ItemHighresLocationProps;

  /**
   * Current state of the highRes deletion operation
   */
  deletionState: HighresDeletionState;

  statusMessage: string;

  /**
   * ISO date string of the time the request was initiated
   */
  initiatedAtTimestamp: string;

  /**
   * Details about the user the deletion request was initiated by
   */
  initiatedBy: {
    userId: string;
    email: string;
  }

  /**
   * ISO date string of the time when the highres should be deleted.
   * Highres is deleted when housekeeping is run which is once every day.
   */
  deletionDate: string;
}

export enum MediaLocationTypes {
  MISSING = 'missing',
  ONLINE = 'online',
  ORIGIN = 'origin',
  ARCHIVE = 'archive',
}

export interface ArchiveInfo {
  /**
   * Date, when the media was archived
   */
  archivedOn: string;
  /**
   * In which storage class the media is now
   */
  storageClass: StorageClass;
  /**
   * After restoring a media from archive it will be online only for a certain period
   */
  mediaOnlineUntil?: string;
  /**
   * Keeps track of the status of restore operation
   */
  restoreStatus?: ArchiveRestoreStatus;
  /**
   * Keeps track of the last restore request to be able to retry restore
   */
  lastRestoreRequest?: RestoreMediaRequestDto;
}

export enum ArchiveRestoreStatus {
  InProgress = 'in-progress',
  Completed = 'completed',
}

export enum StorageClass {
  Standard = 'STANDARD',
  Glacier = 'GLACIER',
  DeepArchive = 'DEEP_ARCHIVE',
}

/**
 * If an item was centrally rendered using Kelda, this can be used to store render specific information
 * on the item.
 */
export interface CentrallyRenderedSequenceInfo {
  /**
   * Name of the AME or DVR folder configured in Kelda that claimed the render request and rendered this item.
   * Note: since this is not an optional field, I am reusing it for DVR despite the name. We can distinguish
   * between the two types by the encoderType field.
   */
  ameWatchFolderName: string;

  /**
   * AME or DVR
   */
  encoderType?: EncoderType;

  /**
   * ID of the user who exported the sequence
   */
  exportedByUserId: string | undefined;
}

